import { useEffect, useState } from 'react';
import { CheckCircle2, Hash } from 'lucide-react';
import { INTEGRATION_SCOPES, type IntegrationScope } from '@repo/shared-types';
import { api, type AvailableSource, type Integration } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { SourcePicker } from './SourcePicker';

/** Slack channels at one destination: routed for the organization, or pinned to a project. */
export function SlackCard({
  scope,
  projectSlug,
  projectName,
  integration,
  onChanged,
}: {
  scope: IntegrationScope;
  projectSlug?: string;
  projectName?: string;
  integration: Integration | null;
  onChanged: () => void;
}) {
  const target = { provider: 'SLACK' as const, scope, ...(projectSlug ? { projectSlug } : {}) };
  const [available, setAvailable] = useState<AvailableSource[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sources = integration?.sources ?? [];

  useEffect(() => {
    api.integrations
      .availableSources(target)
      .then(setAvailable)
      .catch(() => setAvailable([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, projectSlug]);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <Hash size={16} className={sources.length > 0 ? 'text-orange-500' : 'text-ink-faint'} />
        <CardTitle>Slack</CardTitle>
        <span className="text-[11px] text-ink-faint">connected directly, not through the hub</span>
        {sources.length > 0 && (
          <span className="ml-auto flex items-center gap-1 text-xs font-medium text-success-text">
            <CheckCircle2 size={13} />
            {sources.length} {sources.length === 1 ? 'channel' : 'channels'}
          </span>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-xs text-ink-muted leading-relaxed">
          {scope === 'PROJECT'
            ? `Every message in these channels is filed into ${projectName ?? 'this project'}.`
            : INTEGRATION_SCOPES.find((s) => s.scope === 'GLOBAL')!.summary}
        </p>

        {error && <p className="text-sm text-danger-text bg-danger-bg px-3 py-2 rounded-lg">{error}</p>}

        <SourcePicker
          sources={sources}
          available={available}
          busy={busy}
          onChange={(externalIds) => void act(() => api.integrations.setSources({ ...target, externalIds }))}
        />

        {integration && sources.length > 0 && (
          <div className="flex items-center justify-between text-xs text-ink-faint">
            <span>Messages arrive as they are posted</span>
            <button
              onClick={() => void act(() => api.integrations.disconnect(integration.id))}
              disabled={busy}
              className="hover:text-red-500 transition-colors disabled:opacity-40"
            >
              Stop watching all
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
